import React, { useState, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  TextField,
  Divider,
  CircularProgress,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CodeIcon from '@mui/icons-material/Code';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { FaDownload } from 'react-icons/fa';
import InterfaceHeader from '../components/InterfaceHeader';
import FavoriteButton from '../components/FavoriteButton';
import API_BASE_URL, { apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';

const CAMPAIGN_TYPES = [
  { value: 'new-collection', label: 'New Collection' },
  { value: 'seasonal-sale', label: 'Seasonal Sale' },
  { value: 'clearance', label: 'Clearance' },
  { value: 'back-in-stock', label: 'Back in Stock' },
  { value: 'weekly-newsletter', label: 'Weekly Newsletter' },
];

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'warm', label: 'Warm' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'luxurious', label: 'Luxurious' },
];

const CAMPAIGN_DESCRIPTIONS = {
  'new-collection': 'Announce fresh arrivals and new product lines. Creates excitement and be-the-first-to-see energy.',
  'seasonal-sale': 'Tie promotions to the season. Balances value messaging with brand prestige.',
  'clearance': 'Drive urgency with limited-availability messaging. Emphasizes incredible value on remaining inventory.',
  'back-in-stock': 'Notify customers about restocked favorites. Highlights demand and popularity.',
  'weekly-newsletter': 'Curated weekly update with featured products, design tips, and brand story.',
};

const EmailCampaignBuilder = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const primaryColor = TOOL_COLORS.OTHER.primary;
  const primaryHover = TOOL_COLORS.OTHER.hover;
  const primaryLight = TOOL_COLORS.OTHER.light;
  const fileInputRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Single mode state
  const [campaignType, setCampaignType] = useState('new-collection');
  const [collectionName, setCollectionName] = useState('');
  const [itemIds, setItemIds] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [tone, setTone] = useState('professional');
  const [generating, setGenerating] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Batch mode state
  const [batchFile, setBatchFile] = useState(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProcessId, setBatchProcessId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const batchIntervalRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // MJML mode state
  const [mjmlPrompt, setMjmlPrompt] = useState('');
  const [mjmlSubject, setMjmlSubject] = useState('');
  const [mjmlBrandColor, setMjmlBrandColor] = useState('#c9a96e');
  const [mjmlCtaUrl, setMjmlCtaUrl] = useState('https://www.safavieh.com');
  const [mjmlImageUrls, setMjmlImageUrls] = useState(['']);
  const [mjmlCampaignType, setMjmlCampaignType] = useState('urgency-flash-sale');
  const [mjmlDiscountPercent, setMjmlDiscountPercent] = useState('');
  const [mjmlItemIds, setMjmlItemIds] = useState([]);
  const [mjmlItemUrls, setMjmlItemUrls] = useState({});
  const [mjmlItemFileName, setMjmlItemFileName] = useState('');
  const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);
  const [mjmlZoom, setMjmlZoom] = useState(1);
  const mjmlItemFileInputRef = useRef(null);
  const [mjmlGenerating, setMjmlGenerating] = useState(false);
  const [mjmlResult, setMjmlResult] = useState(null);
  const [mjmlPreviewMode, setMjmlPreviewMode] = useState('preview');
  const [isDraggingMjml, setIsDraggingMjml] = useState(false);

  // History state
  const [historyTemplates, setHistoryTemplates] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historySearch, setHistorySearch] = useState('');
  const [historyFavoritesOnly, setHistoryFavoritesOnly] = useState(false);
  const [historyCampaignType, setHistoryCampaignType] = useState('');
  const [historyLoadingTemplate, setHistoryLoadingTemplate] = useState(null);
  const [historyRenameDialog, setHistoryRenameDialog] = useState({ open: false, template: null, newName: '' });

  const showDiscount = campaignType === 'seasonal-sale' || campaignType === 'clearance';

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      '& fieldset': { borderColor: isDarkMode ? alpha('#fff', 0.23) : alpha('#000', 0.23) },
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  };

  // --- Single Generate ---
  const handleGenerate = async () => {
    if (!collectionName.trim()) {
      setError('Please enter a collection or event name.');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);
    setEmailResult(null);

    try {
      const payload = {
        campaignType,
        collectionName: collectionName.trim(),
        itemIds: itemIds.trim() || null,
        discountPercent: showDiscount && discountPercent ? parseInt(discountPercent) : null,
        tone,
      };

      const data = await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (data.success) {
        setEmailResult(data.email);
        setSuccess('Email campaign generated successfully!');
      } else {
        setError(data.message || 'Failed to generate email campaign.');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // --- MJML Generate ---
  const addImageUrlField = () => setMjmlImageUrls((prev) => [...prev, '']);
  const updateImageUrl = (idx, value) => {
    setMjmlImageUrls((prev) => prev.map((u, i) => (i === idx ? value : u)));
  };
  const removeImageUrl = (idx) => {
    setMjmlImageUrls((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [''] : next;
    });
  };
  const pasteImageUrls = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (text.includes('\n') || text.includes(',')) {
      e.preventDefault();
      const urls = text.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean);
      if (urls.length > 0) {
        setMjmlImageUrls((prev) => {
          const existing = prev.filter(Boolean);
          return [...existing, ...urls, ''];
        });
      }
    }
  };

  const handleGenerateMjml = async () => {
    if (!mjmlPrompt.trim() && !mjmlCampaignType) {
      setError('Please pick a campaign type or enter a prompt.');
      return;
    }

    setMjmlGenerating(true);
    setError(null);
    setSuccess(null);
    setMjmlResult(null);

    try {
      const cleanImages = mjmlImageUrls.map((u) => u.trim()).filter(Boolean);
      const payload = {
        prompt: mjmlPrompt.trim(),
        imageUrls: cleanImages,
        subject: mjmlSubject.trim() || null,
        brandColor: mjmlBrandColor.trim() || null,
        ctaUrl: mjmlCtaUrl.trim() || null,
        campaignType: mjmlCampaignType || null,
        discountPercent: mjmlDiscountPercent ? parseInt(mjmlDiscountPercent, 10) : null,
        itemIds: mjmlItemIds,
        itemUrls: mjmlItemUrls,
      };

      const data = await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/generate-mjml`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (data.success) {
        setMjmlResult(data);
        setSuccess('MJML email generated successfully!');
      } else {
        setError(data.message || 'Failed to generate MJML email.');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setMjmlGenerating(false);
    }
  };

  const downloadMjmlItemTemplate = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const res = await fetch(`${API_BASE_URL}/api/EmailCampaignBuilder/mjml-template`, {
        method: 'GET',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mjml-email-itemids-template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download template: ${err.message}`);
    }
  };

  const processMjmlItemFile = async (file) => {
    if (!file) return;
    const validExt = /\.(xlsx|xls|csv)$/i;
    if (!validExt.test(file.name)) {
      setError('Please drop an Excel or CSV file (.xlsx, .xls, .csv)');
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!rows.length) throw new Error('File is empty.');

      const header = rows[0].map((c) => (c || '').toString().trim().toLowerCase());
      const itemIdCol = header.findIndex((h) => h === 'itemid' || h === 'item id' || h === 'id');
      const urlCol = header.findIndex((h) => h === 'producturl' || h === 'product url' || h === 'url' || h === 'link');
      if (itemIdCol === -1) throw new Error('No "ItemID" column found in the first row.');

      const ids = [];
      const urls = {};
      for (let i = 1; i < rows.length; i++) {
        const v = (rows[i][itemIdCol] || '').toString().trim();
        if (!v) continue;
        ids.push(v);
        if (urlCol !== -1) {
          const u = (rows[i][urlCol] || '').toString().trim();
          if (u) urls[v] = u;
        }
      }
      const unique = Array.from(new Set(ids));
      setMjmlItemIds(unique);
      setMjmlItemUrls(urls);
      setMjmlItemFileName(file.name);
      const urlCount = Object.keys(urls).length;
      setSuccess(`Loaded ${unique.length} ItemID${unique.length !== 1 ? 's' : ''}${urlCount ? ` (${urlCount} with URL)` : ''} from ${file.name}`);
      setError(null);
    } catch (err) {
      setError(`Failed to read ItemIDs: ${err.message}`);
      setMjmlItemIds([]);
      setMjmlItemFileName('');
    } finally {
      if (mjmlItemFileInputRef.current) mjmlItemFileInputRef.current.value = '';
    }
  };

  const handleMjmlItemFileUpload = (e) => {
    const file = e.target.files?.[0];
    processMjmlItemFile(file);
  };

  const handleMjmlDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingMjml(true); };
  const handleMjmlDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingMjml(false); };
  const handleMjmlDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleMjmlDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMjml(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processMjmlItemFile(f);
  };

  const clearMjmlItemIds = () => {
    setMjmlItemIds([]);
    setMjmlItemUrls({});
    setMjmlItemFileName('');
  };

  // --- Template History ---
  const fetchHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (historySearch.trim()) params.set('search', historySearch.trim());
      if (historyCampaignType) params.set('campaignType', historyCampaignType);
      if (historyFavoritesOnly) params.set('favoritesOnly', 'true');
      const data = await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/templates?${params.toString()}`);
      if (data.success) {
        setHistoryTemplates(data.templates || []);
        setHistoryTotal(data.total || 0);
        setHistoryPage(data.page || page);
      }
    } catch (err) {
      setError(`Failed to load history: ${err.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Auto-refresh history when the tab is opened, search changes, or filters flip.
  React.useEffect(() => {
    if (activeTab === 3) {
      fetchHistory(1);
    }
  }, [activeTab, historySearch, historyCampaignType, historyFavoritesOnly]); // fetchHistory intentionally omitted — it closes over these deps

  const loadTemplateIntoEditor = async (templateId) => {
    setHistoryLoadingTemplate(templateId);
    try {
      const data = await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/templates/${templateId}`);
      if (!data.success || !data.template) {
        throw new Error(data.message || 'Template not found');
      }
      const t = data.template;
      // Jump to the MJML tab
      setActiveTab(2);
      // Fill all the MJML-mode inputs from the saved template
      setMjmlPrompt(t.prompt || '');
      setMjmlSubject(t.subject || '');
      setMjmlBrandColor(t.brandColor || '#c9a96e');
      setMjmlCtaUrl(t.ctaUrl || 'https://www.safavieh.com');
      setMjmlImageUrls((t.imageUrls && t.imageUrls.length > 0) ? t.imageUrls : ['']);
      setMjmlCampaignType(t.campaignType || 'urgency-flash-sale');
      setMjmlDiscountPercent(t.discountPercent != null ? String(t.discountPercent) : '');
      setMjmlItemIds(t.itemIds || []);
      setMjmlItemUrls(t.itemUrls || {});
      setMjmlItemFileName(t.itemIds && t.itemIds.length > 0 ? `Reused from: ${t.name}` : '');
      // Surface the previously generated MJML/HTML as the current result so the user sees the preview
      setMjmlResult({
        mjml: t.mjml,
        html: t.html,
        notes: t.notes,
        prompt: t.prompt,
        items: t.items || [],
        missingItemIds: [],
      });
      setSuccess(`Loaded "${t.name}". Edit and re-generate, or download the existing version.`);
      setError(null);
    } catch (err) {
      setError(`Failed to load template: ${err.message}`);
    } finally {
      setHistoryLoadingTemplate(null);
    }
  };

  const toggleTemplateFavorite = async (template) => {
    const nextFav = !template.isFavorite;
    try {
      await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/templates/${template.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isFavorite: nextFav }),
      });
      setHistoryTemplates(prev => prev.map(t => t.id === template.id ? { ...t, isFavorite: nextFav } : t));
    } catch (err) {
      setError(`Failed to update favorite: ${err.message}`);
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return;
    try {
      await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/templates/${template.id}`, {
        method: 'DELETE',
      });
      setHistoryTemplates(prev => prev.filter(t => t.id !== template.id));
      setHistoryTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      setError(`Failed to delete template: ${err.message}`);
    }
  };

  const openRenameDialog = (template) => {
    setHistoryRenameDialog({ open: true, template, newName: template.name || '' });
  };

  const commitRename = async () => {
    const { template, newName } = historyRenameDialog;
    if (!template || !newName.trim()) return;
    try {
      await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/templates/${template.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      setHistoryTemplates(prev => prev.map(t => t.id === template.id ? { ...t, name: newName.trim() } : t));
      setHistoryRenameDialog({ open: false, template: null, newName: '' });
    } catch (err) {
      setError(`Failed to rename template: ${err.message}`);
    }
  };

  const downloadMjmlFile = () => {
    if (!mjmlResult?.mjml) return;
    const blob = new Blob([mjmlResult.mjml], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safavieh-email-${Date.now()}.mjml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // --- Batch Mode ---
  const validateAndSetFile = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls'];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) {
      setError('Please select a valid Excel (.xlsx, .xls) file');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }
    setBatchFile(selectedFile);
    setError(null);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const startBatchProcessing = async () => {
    if (!batchFile) { setError('Please select a file first'); return; }
    downloadTriggeredRef.current = false;
    setBatchStatus(null); setBatchProcessId(null);
    if (batchIntervalRef.current) { clearInterval(batchIntervalRef.current); batchIntervalRef.current = null; }

    const formData = new FormData();
    formData.append('file', batchFile);

    try {
      setBatchProcessing(true); setError(null); setSuccess(null);
      const data = await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/generate-batch`, {
        method: 'POST',
        body: formData,
      });
      if (data.success) {
        setBatchProcessId(data.processId);
        batchIntervalRef.current = setInterval(() => fetchBatchStatus(data.processId), 1500);
      } else {
        setError(data.message || 'Failed to start batch processing');
        setBatchProcessing(false);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      setBatchProcessing(false);
    }
  };

  const fetchBatchStatus = async (pId) => {
    try {
      const data = await apiCall(`${API_BASE_URL}/api/EmailCampaignBuilder/status/${pId}`, { method: 'GET' });
      if (data.success) {
        const s = data.status;
        setBatchStatus(s);
        const st = s.status ?? s.Status ?? '';
        if (st === 'completed' || st === 'error') {
          setBatchProcessing(false);
          if (batchIntervalRef.current) { clearInterval(batchIntervalRef.current); batchIntervalRef.current = null; }
          if (st === 'completed') {
            setSuccess('Batch email campaign generation completed!');
            const awsUrl = s.awsUrl ?? s.AwsUrl ?? null;
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              if (awsUrl) {
                const link = document.createElement('a');
                link.href = awsUrl;
                link.download = 'Email Campaign Results.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } else {
                downloadBatchResult(pId);
              }
            }
          } else {
            setError('Batch processing failed.');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching batch status:', err);
    }
  };

  const downloadBatchResult = async (pId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/EmailCampaignBuilder/download/${pId}`, {
        method: 'GET',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const now = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      });
      const filename = `Email Campaign Results ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(`Download failed: ${err.message}`); }
  };

  const downloadSample = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/EmailCampaignBuilder/sample`, {
        method: 'GET',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const now = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      });
      const filename = `Email Campaign Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(`Template download failed: ${err.message}`); }
  };

  const clearFile = () => { setBatchFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const handleReset = () => {
    setCampaignType('new-collection'); setCollectionName(''); setItemIds('');
    setDiscountPercent(''); setTone('professional'); setGenerating(false);
    setEmailResult(null); setError(null); setSuccess(null);
    setBatchFile(null); setBatchProcessing(false); setBatchProcessId(null); setBatchStatus(null);
    if (batchIntervalRef.current) { clearInterval(batchIntervalRef.current); batchIntervalRef.current = null; }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Batch status helpers
  const batchStatusVal = batchStatus?.status ?? batchStatus?.Status ?? '';
  const batchProgressPct = batchStatus?.progressPercentage ?? batchStatus?.ProgressPercentage ?? 0;
  const batchTotalItems = batchStatus?.totalItems ?? batchStatus?.TotalItems ?? 0;
  const batchProcessedItems = batchStatus?.processedItems ?? batchStatus?.ProcessedItems ?? 0;
  const batchFailedItems = batchStatus?.failedItems ?? batchStatus?.FailedItems ?? 0;
  const batchLogs = batchStatus?.logs ?? batchStatus?.Logs ?? [];

  // --- Render helpers ---
  const renderCopyButton = (text, field) => (
    <Tooltip title={copiedField === field ? 'Copied!' : 'Copy'}>
      <IconButton
        size="small"
        onClick={() => copyToClipboard(text, field)}
        sx={{ color: copiedField === field ? '#4caf50' : 'text.secondary', ml: 1 }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderEmailPreview = () => {
    if (!emailResult) return null;
    return (
      <Paper sx={{ p: 3, mt: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>
          Generated Email Campaign
        </Typography>

        {/* Subject Lines */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Subject Lines (A/B Test)</Typography>
          {[
            { label: 'A', value: emailResult.subjectLineA },
            { label: 'B', value: emailResult.subjectLineB },
            { label: 'C', value: emailResult.subjectLineC },
          ].map((s) => (
            <Box key={s.label} sx={{
              display: 'flex', alignItems: 'center', p: 1, mb: 0.5,
              bgcolor: isDarkMode ? alpha('#fff', 0.04) : alpha('#000', 0.02),
              borderRadius: 1, border: `1px solid ${theme.palette.divider}`,
            }}>
              <Chip label={s.label} size="small" sx={{
                fontWeight: 700, mr: 1, backgroundColor: alpha(primaryColor, 0.15), color: primaryColor,
              }} />
              <Typography variant="body2" sx={{ flex: 1 }}>{s.value}</Typography>
              {renderCopyButton(s.value, `subject-${s.label}`)}
            </Box>
          ))}
        </Box>

        {/* Preview Text */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Preview Text</Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', p: 1.5,
            bgcolor: isDarkMode ? alpha('#fff', 0.04) : alpha('#000', 0.02),
            borderRadius: 1, border: `1px solid ${theme.palette.divider}`,
          }}>
            <Typography variant="body2" sx={{ flex: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              {emailResult.previewText}
            </Typography>
            {renderCopyButton(emailResult.previewText, 'preview')}
          </Box>
        </Box>

        {/* Hero Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Hero Section</Typography>
          <Box sx={{
            p: 2.5, borderRadius: 1, background: isDarkMode
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#fff', position: 'relative',
          }}>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              {emailResult.heroSection}
            </Typography>
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              {renderCopyButton(emailResult.heroSection, 'hero')}
            </Box>
          </Box>
        </Box>

        {/* Product Showcase */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Product Showcase</Typography>
          <Box sx={{
            p: 2, borderRadius: 1,
            bgcolor: isDarkMode ? alpha('#fff', 0.04) : alpha('#000', 0.02),
            border: `1px solid ${theme.palette.divider}`, position: 'relative',
          }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {emailResult.productShowcase}
            </Typography>
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              {renderCopyButton(emailResult.productShowcase, 'showcase')}
            </Box>
          </Box>
        </Box>

        {/* CTA */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>CTA Button Options</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(emailResult.ctaText || '').split('\n').filter(Boolean).map((cta, i) => (
              <Chip
                key={i}
                label={cta.trim()}
                onClick={() => copyToClipboard(cta.trim(), `cta-${i}`)}
                sx={{
                  fontWeight: 600, cursor: 'pointer',
                  backgroundColor: alpha(primaryColor, 0.15), color: primaryColor,
                  border: copiedField === `cta-${i}` ? `2px solid #4caf50` : `1px solid ${alpha(primaryColor, 0.3)}`,
                  '&:hover': { backgroundColor: alpha(primaryColor, 0.25) },
                }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Full HTML Preview */}
        {emailResult.fullHtmlPreview && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Full Email Preview</Typography>
              <Button
                size="small"
                onClick={() => copyToClipboard(emailResult.fullHtmlPreview, 'html')}
                sx={{ color: primaryColor, textTransform: 'none' }}
                startIcon={<ContentCopyIcon fontSize="small" />}
              >
                {copiedField === 'html' ? 'Copied!' : 'Copy HTML'}
              </Button>
            </Box>
            <Box sx={{
              border: `1px solid ${theme.palette.divider}`, borderRadius: 1,
              overflow: 'hidden', maxHeight: 500, overflowY: 'auto', bgcolor: '#fff',
            }}>
              <Box
                component="iframe"
                srcDoc={emailResult.fullHtmlPreview}
                title="Email Preview"
                sx={{
                  width: '100%',
                  height: { xs: 320, sm: 400, md: 450 },
                  border: 'none',
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1200, mx: 'auto', position: 'relative' }}>
      <FavoriteButton interfaceName="Email Campaign Builder" category="others" />
      <InterfaceHeader
        title="EMAIL CAMPAIGN BUILDER"
        color={primaryColor}
        category="others"
      />

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Tabs */}
          <Paper sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minHeight: 48,
                  minWidth: { xs: 'auto', sm: 90 },
                  px: { xs: 1.5, sm: 2 },
                },
                '& .Mui-selected': { color: primaryColor },
                '& .MuiTabs-indicator': { backgroundColor: primaryColor },
              }}
            >
              <Tab label="Single Campaign" icon={<EmailIcon />} iconPosition="start" />
              <Tab label="Batch Mode" icon={<DescriptionIcon />} iconPosition="start" />
              <Tab label="MJML Email" icon={<CodeIcon />} iconPosition="start" />
              <Tab label="History" icon={<HistoryIcon />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
              {/* ========== SINGLE MODE ========== */}
              {activeTab === 0 && (
                <>
                  {/* Campaign Type */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Campaign Type</Typography>
                    <ToggleButtonGroup
                      value={campaignType}
                      exclusive
                      onChange={(e, v) => { if (v) setCampaignType(v); }}
                      size="small"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {CAMPAIGN_TYPES.map((ct) => (
                        <ToggleButton key={ct.value} value={ct.value} sx={{
                          textTransform: 'none', fontWeight: 600, px: 2,
                          '&.Mui-selected': { backgroundColor: alpha(primaryColor, 0.15), color: primaryColor, borderColor: primaryColor },
                          '&.Mui-selected:hover': { backgroundColor: alpha(primaryColor, 0.25) },
                        }}>
                          {ct.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  {/* Collection Name */}
                  <Box sx={{ mb: 2.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Collection / Event Name"
                      value={collectionName}
                      onChange={(e) => setCollectionName(e.target.value)}
                      placeholder="e.g., Spring 2026 Outdoor Collection"
                      sx={textFieldStyles}
                    />
                  </Box>

                  {/* Item IDs */}
                  <Box sx={{ mb: 2.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Item IDs (optional)"
                      value={itemIds}
                      onChange={(e) => setItemIds(e.target.value)}
                      placeholder="e.g., OUT101, OUT102, OUT103"
                      multiline
                      minRows={2}
                      sx={textFieldStyles}
                      helperText="Comma-separated list of item IDs to feature"
                    />
                  </Box>

                  {/* Discount % (conditional) */}
                  {showDiscount && (
                    <Box sx={{ mb: 2.5 }}>
                      <TextField
                        size="small"
                        label="Discount %"
                        value={discountPercent}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '');
                          if (v === '' || (parseInt(v) > 0 && parseInt(v) <= 100)) setDiscountPercent(v);
                        }}
                        placeholder="e.g., 30"
                        sx={{ width: { xs: '100%', sm: 150 }, ...textFieldStyles }}
                        helperText="Optional discount percentage"
                      />
                    </Box>
                  )}

                  {/* Tone */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tone</Typography>
                    <ToggleButtonGroup
                      value={tone}
                      exclusive
                      onChange={(e, v) => { if (v) setTone(v); }}
                      size="small"
                      sx={{ flexWrap: 'wrap', '& .MuiToggleButtonGroup-grouped': { border: `1px solid ${theme.palette.divider} !important`, borderRadius: '4px !important', mx: '2px !important', my: '2px !important' } }}
                    >
                      {TONES.map((t) => (
                        <ToggleButton key={t.value} value={t.value} sx={{
                          textTransform: 'none', fontWeight: 600, px: { xs: 1.5, sm: 2.5 },
                          '&.Mui-selected': { backgroundColor: alpha(primaryColor, 0.15), color: primaryColor, borderColor: primaryColor },
                          '&.Mui-selected:hover': { backgroundColor: alpha(primaryColor, 0.25) },
                        }}>
                          {t.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Generate Button */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                      onClick={handleGenerate}
                      disabled={generating || !collectionName.trim()}
                      sx={{
                        flex: 1, py: 1.2, backgroundColor: primaryColor, color: 'white',
                        textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: primaryHover },
                        '&:disabled': {
                          backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300],
                          color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500],
                        },
                      }}
                    >
                      {generating ? 'Generating...' : 'GENERATE EMAIL'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      sx={{
                        py: 1.2, textTransform: 'none', fontWeight: 600,
                        borderColor: primaryColor, color: primaryColor,
                        '&:hover': { borderColor: primaryHover, bgcolor: primaryLight },
                      }}
                    >
                      RESET
                    </Button>
                  </Box>
                </>
              )}

              {/* ========== BATCH MODE ========== */}
              {activeTab === 1 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Upload Excel File with Campaigns
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Required columns: CampaignType, CollectionName. Optional: ItemIds, DiscountPercent, Tone
                  </Typography>

                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />

                  <Box
                    onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver} onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? primaryColor : batchFile ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                      bgcolor: isDragging
                        ? primaryLight
                        : batchFile
                          ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      transition: 'all 0.3s ease', mb: 2,
                      '&:hover': {
                        borderColor: batchFile ? '#4CAF50' : primaryColor,
                        bgcolor: batchFile
                          ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : primaryLight,
                      },
                    }}
                  >
                    {!batchFile ? (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? primaryColor : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging ? 'Drop file here' : 'Drop your Excel file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>1 file selected</Typography>
                        <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                      </>
                    )}
                  </Box>

                  {/* File Info Bar */}
                  {batchFile && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1, mb: 1,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <DescriptionIcon sx={{ color: primaryColor, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {batchFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(batchFile.size / 1024).toFixed(1)} KB)
                        </Typography>
                      </Box>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); clearFile(); }}
                        sx={{ color: theme.palette.error.main, minWidth: 'auto', p: 0.5 }}>
                        <CloseIcon fontSize="small" />
                      </Button>
                    </Box>
                  )}

                  {/* Batch Action Row */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={batchProcessing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                      onClick={startBatchProcessing}
                      disabled={batchProcessing || !batchFile}
                      sx={{
                        flex: 1, py: 1.2, backgroundColor: primaryColor, color: 'white',
                        textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: primaryHover },
                        '&:disabled': {
                          backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300],
                          color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500],
                        },
                      }}
                    >
                      {batchProcessing ? 'Processing...' : 'PROCESS BATCH'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<FaDownload />}
                      onClick={downloadSample}
                      sx={{
                        py: 1.2, textTransform: 'none', fontWeight: 600,
                        borderColor: primaryColor, color: primaryColor,
                        '&:hover': { borderColor: primaryHover, bgcolor: primaryLight },
                      }}
                    >
                      DOWNLOAD TEMPLATE
                    </Button>
                  </Box>
                </>
              )}

              {/* ========== MJML MODE ========== */}
              {activeTab === 2 && (
                <>
                  {/* Campaign Type */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Campaign Type</Typography>
                    <ToggleButtonGroup
                      value={mjmlCampaignType}
                      exclusive
                      onChange={(e, v) => { if (v) setMjmlCampaignType(v); }}
                      size="small"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {[
                        { value: 'urgency-flash-sale', label: 'Flash Sale' },
                        { value: 'social-proof-trending', label: "What's Trending" },
                        { value: 'room-transformation', label: 'Room Transformation' },
                        { value: 'designer-curated', label: "Designer's Desk" },
                        { value: 'new-arrivals-first-look', label: 'VIP First-Look' },
                      ].map((ct) => (
                        <ToggleButton key={ct.value} value={ct.value} sx={{
                          textTransform: 'none', fontWeight: 600, px: 2,
                          '&.Mui-selected': { backgroundColor: alpha(primaryColor, 0.15), color: primaryColor, borderColor: primaryColor },
                          '&.Mui-selected:hover': { backgroundColor: alpha(primaryColor, 0.25) },
                        }}>
                          {ct.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  {mjmlCampaignType === 'urgency-flash-sale' && (
                    <Box sx={{ mb: 2.5, maxWidth: 220 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Discount %"
                        placeholder="e.g. 70"
                        value={mjmlDiscountPercent}
                        onChange={(e) => setMjmlDiscountPercent(e.target.value)}
                        inputProps={{ min: 0, max: 99 }}
                        sx={textFieldStyles}
                      />
                    </Box>
                  )}

                  {/* Product ItemIDs — pulls spec data from Items Detail */}
                  <Box sx={{ mb: 2.5, p: 2, borderRadius: 1.5, border: `1px dashed ${theme.palette.divider}`, bgcolor: isDarkMode ? alpha('#fff', 0.02) : alpha(primaryColor, 0.03) }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Product Data (optional but strongly recommended)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Download the template, fill in your ItemIDs, then upload it. The system pulls each item's real specs (color, material, size, style, collection, etc.) from the Items Detail database and feeds them to the AI — this is the single biggest lever for fixing AI-slop copy.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FaDownload />}
                        onClick={downloadMjmlItemTemplate}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: primaryColor, color: primaryColor, '&:hover': { borderColor: primaryHover, bgcolor: primaryLight } }}
                      >
                        Download Template
                      </Button>
                      {mjmlItemIds.length > 0 && (
                        <Chip
                          label={`${mjmlItemIds.length} ItemID${mjmlItemIds.length !== 1 ? 's' : ''}${mjmlItemFileName ? ` · ${mjmlItemFileName}` : ''}`}
                          onDelete={clearMjmlItemIds}
                          sx={{ bgcolor: alpha(primaryColor, 0.12), color: primaryColor, fontWeight: 600 }}
                        />
                      )}
                    </Box>

                    {/* Drag-and-drop zone for the filled template */}
                    <input
                      ref={mjmlItemFileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      style={{ display: 'none' }}
                      onChange={handleMjmlItemFileUpload}
                    />
                    <Box
                      onDragEnter={handleMjmlDragEnter}
                      onDragOver={handleMjmlDragOver}
                      onDragLeave={handleMjmlDragLeave}
                      onDrop={handleMjmlDrop}
                      onClick={() => mjmlItemFileInputRef.current?.click()}
                      sx={{
                        border: `2px dashed ${isDraggingMjml ? primaryColor : mjmlItemIds.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2.5,
                        textAlign: 'center',
                        bgcolor: isDraggingMjml
                          ? primaryLight
                          : mjmlItemIds.length > 0
                            ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                            : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: mjmlItemIds.length > 0 ? '#4CAF50' : primaryColor,
                          bgcolor: mjmlItemIds.length > 0
                            ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                            : primaryLight,
                        }
                      }}
                    >
                      {mjmlItemIds.length === 0 ? (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 36, color: isDraggingMjml ? primaryColor : 'text.secondary', mb: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {isDraggingMjml ? 'Drop file here' : 'Drop your filled template here or click to browse'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Excel (.xlsx, .xls) or CSV with an ItemID column — optional ProductUrl column supported
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 36, color: '#4CAF50', mb: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {mjmlItemIds.length} ItemID{mjmlItemIds.length !== 1 ? 's' : ''} loaded
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Click or drop to replace
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Prompt */}
                  <Box sx={{ mb: 2.5 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={12}
                      label="Email Prompt (optional — campaign type steers the creative)"
                      placeholder="Optional extra direction — e.g. 'Focus on the Adirondack Collection and mention Free Shipping on $49+'. Leave blank to let Claude use the campaign type on its own."
                      value={mjmlPrompt}
                      onChange={(e) => setMjmlPrompt(e.target.value)}
                      sx={textFieldStyles}
                      helperText="Optional. The campaign type above drives the voice, structure, and hook."
                    />
                  </Box>

                  {/* Subject + Brand Color + CTA URL row */}
                  <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Subject Line (optional)"
                        placeholder="e.g., Spring has arrived"
                        value={mjmlSubject}
                        onChange={(e) => setMjmlSubject(e.target.value)}
                        sx={textFieldStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Brand Color"
                        placeholder="#c9a96e"
                        value={mjmlBrandColor}
                        onChange={(e) => setMjmlBrandColor(e.target.value)}
                        sx={textFieldStyles}
                        InputProps={{
                          startAdornment: (
                            <Tooltip title="Click to pick a color">
                              <Box sx={{ position: 'relative', mr: 1, flexShrink: 0 }}>
                                <Box
                                  component="label"
                                  htmlFor="mjml-brand-color-picker"
                                  sx={{
                                    display: 'block',
                                    width: 22, height: 22, borderRadius: '4px',
                                    bgcolor: mjmlBrandColor || '#c9a96e',
                                    border: `1px solid ${theme.palette.divider}`,
                                    cursor: 'pointer',
                                    transition: 'transform 0.15s',
                                    '&:hover': { transform: 'scale(1.1)' },
                                  }}
                                />
                                <input
                                  id="mjml-brand-color-picker"
                                  type="color"
                                  value={/^#[0-9A-Fa-f]{6}$/.test(mjmlBrandColor) ? mjmlBrandColor : '#c9a96e'}
                                  onChange={(e) => setMjmlBrandColor(e.target.value)}
                                  style={{
                                    position: 'absolute',
                                    top: 0, left: 0,
                                    width: 22, height: 22,
                                    opacity: 0,
                                    cursor: 'pointer',
                                    padding: 0, border: 'none', background: 'none',
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="CTA Button URL"
                        placeholder="https://www.safavieh.com"
                        value={mjmlCtaUrl}
                        onChange={(e) => setMjmlCtaUrl(e.target.value)}
                        sx={textFieldStyles}
                      />
                    </Grid>
                  </Grid>

                  {/* Image URLs */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Image URLs
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({mjmlImageUrls.filter(Boolean).length} image{mjmlImageUrls.filter(Boolean).length !== 1 ? 's' : ''})
                        </Typography>
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddPhotoAlternateIcon />}
                        onClick={addImageUrlField}
                        sx={{
                          textTransform: 'none', fontWeight: 600, color: primaryColor,
                          '&:hover': { bgcolor: primaryLight },
                        }}
                      >
                        Add another
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Paste a list (comma or newline separated) to add multiple at once.
                    </Typography>

                    {mjmlImageUrls.map((url, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minWidth: 0 }}>
                        <Box sx={{
                          width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, borderRadius: '6px', flexShrink: 0,
                          border: `1px solid ${theme.palette.divider}`, overflow: 'hidden',
                          bgcolor: isDarkMode ? alpha('#fff', 0.04) : alpha('#000', 0.02),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {url ? (
                            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <AddPhotoAlternateIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                          )}
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`Image URL ${idx + 1}`}
                          value={url}
                          onChange={(e) => updateImageUrl(idx, e.target.value)}
                          onPaste={pasteImageUrls}
                          sx={textFieldStyles}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeImageUrl(idx)}
                          sx={{ color: theme.palette.error.main }}
                          disabled={mjmlImageUrls.length === 1 && !url}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={mjmlGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                      onClick={handleGenerateMjml}
                      disabled={mjmlGenerating || (!mjmlPrompt.trim() && !mjmlCampaignType)}
                      sx={{
                        flex: 1, py: 1.2, backgroundColor: primaryColor, color: 'white',
                        textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: primaryHover },
                        '&:disabled': {
                          backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300],
                          color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500],
                        },
                      }}
                    >
                      {mjmlGenerating ? 'Generating MJML...' : 'GENERATE MJML EMAIL'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setMjmlPrompt(''); setMjmlSubject(''); setMjmlImageUrls(['']);
                        setMjmlResult(null); setError(null); setSuccess(null);
                      }}
                      sx={{
                        py: 1.2, textTransform: 'none', fontWeight: 600,
                        borderColor: primaryColor, color: primaryColor,
                        '&:hover': { borderColor: primaryHover, bgcolor: primaryLight },
                      }}
                    >
                      RESET
                    </Button>
                  </Box>
                </>
              )}

              {/* ========== HISTORY ========== */}
              {activeTab === 3 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Every MJML email you generate is saved here automatically so you can reuse it for
                    new products, different images, or seasonal sales. Click a template to load it back
                    into the MJML editor — change the items, images, or discount, and regenerate.
                  </Typography>

                  {/* Filters */}
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search by name, subject, prompt..."
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      sx={{ ...textFieldStyles, flex: 1, minWidth: 220 }}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />,
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Campaign Type</InputLabel>
                      <Select
                        label="Campaign Type"
                        value={historyCampaignType}
                        onChange={(e) => setHistoryCampaignType(e.target.value)}
                      >
                        <MenuItem value="">All types</MenuItem>
                        <MenuItem value="urgency-flash-sale">Flash Sale</MenuItem>
                        <MenuItem value="social-proof-trending">Social Proof</MenuItem>
                        <MenuItem value="room-transformation">Room Transform</MenuItem>
                        <MenuItem value="designer-curated">Designer Curated</MenuItem>
                        <MenuItem value="new-arrivals-first-look">First Look</MenuItem>
                      </Select>
                    </FormControl>
                    <ToggleButton
                      value="favorites"
                      selected={historyFavoritesOnly}
                      onChange={() => setHistoryFavoritesOnly(v => !v)}
                      size="small"
                      sx={{
                        textTransform: 'none',
                        '&.Mui-selected': { bgcolor: primaryLight, color: primaryColor, borderColor: primaryColor },
                      }}
                    >
                      {historyFavoritesOnly ? <StarIcon sx={{ mr: 0.5, fontSize: 18 }} /> : <StarBorderIcon sx={{ mr: 0.5, fontSize: 18 }} />}
                      Favorites
                    </ToggleButton>
                    <Button size="small" onClick={() => fetchHistory(1)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                      Refresh
                    </Button>
                  </Box>

                  {historyLoading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={28} sx={{ color: primaryColor }} />
                    </Box>
                  )}

                  {!historyLoading && historyTemplates.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      <HistoryIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                      <Typography variant="body1">No saved templates yet</Typography>
                      <Typography variant="caption">
                        Generate an MJML email from the MJML tab and it'll show up here.
                      </Typography>
                    </Box>
                  )}

                  {!historyLoading && historyTemplates.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {historyTemplates.map((t) => (
                        <Paper
                          key={t.id}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: primaryColor, bgcolor: primaryLight },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            {/* Brand color swatch */}
                            <Box sx={{
                              width: 40, height: 40, borderRadius: 1, flexShrink: 0,
                              bgcolor: t.brandColor || '#c9a96e',
                              border: `1px solid ${theme.palette.divider}`,
                            }} />
                            {/* Info */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {t.name}
                                </Typography>
                                {t.campaignType && (
                                  <Chip label={t.campaignType.replace(/-/g, ' ')} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                                {t.discountPercent != null && (
                                  <Chip label={`${t.discountPercent}% off`} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                                {t.itemCount > 0 && (
                                  <Chip label={`${t.itemCount} item${t.itemCount !== 1 ? 's' : ''}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                              {t.subject && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', fontWeight: 500, mb: 0.25 }}>
                                  Subject: {t.subject}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Created {t.createdAt}
                              </Typography>
                            </Box>
                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                              <Tooltip title={t.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                                <IconButton size="small" onClick={() => toggleTemplateFavorite(t)} sx={{ color: t.isFavorite ? '#f59e0b' : 'text.secondary' }}>
                                  {t.isFavorite ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Rename">
                                <IconButton size="small" onClick={() => openRenameDialog(t)} sx={{ color: 'text.secondary' }}>
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Load into MJML editor">
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={historyLoadingTemplate === t.id}
                                    onClick={() => loadTemplateIntoEditor(t.id)}
                                    startIcon={historyLoadingTemplate === t.id ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                    sx={{
                                      bgcolor: primaryColor, color: 'white', textTransform: 'none', fontWeight: 600,
                                      '&:hover': { bgcolor: primaryHover },
                                    }}
                                  >
                                    Load
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => deleteTemplate(t)} sx={{ color: 'error.main' }}>
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {historyTotal > 20 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                      <Button size="small" disabled={historyPage <= 1} onClick={() => fetchHistory(historyPage - 1)}>
                        Previous
                      </Button>
                      <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        Page {historyPage} of {Math.max(1, Math.ceil(historyTotal / 20))}
                      </Typography>
                      <Button
                        size="small"
                        disabled={historyPage >= Math.ceil(historyTotal / 20)}
                        onClick={() => fetchHistory(historyPage + 1)}
                      >
                        Next
                      </Button>
                    </Box>
                  )}

                  {/* Rename dialog */}
                  <Dialog
                    open={historyRenameDialog.open}
                    onClose={() => setHistoryRenameDialog({ open: false, template: null, newName: '' })}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogTitle>Rename template</DialogTitle>
                    <DialogContent>
                      <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="New name"
                        value={historyRenameDialog.newName}
                        onChange={(e) => setHistoryRenameDialog(s => ({ ...s, newName: e.target.value }))}
                        sx={{ ...textFieldStyles, mt: 1 }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setHistoryRenameDialog({ open: false, template: null, newName: '' })}>
                        Cancel
                      </Button>
                      <Button onClick={commitRename} variant="contained" sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: primaryHover } }}>
                        Save
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Batch Processing Status */}
          {activeTab === 1 && batchStatus && (
            <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>Processing Status</Typography>
                <Chip
                  label={batchStatusVal === 'completed' ? 'Completed' : batchStatusVal === 'error' ? 'Error' : 'Processing...'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: batchStatusVal === 'completed' ? alpha('#4caf50', 0.15) : batchStatusVal === 'error' ? alpha('#f44336', 0.15) : alpha(primaryColor, 0.15),
                    color: batchStatusVal === 'completed' ? '#4caf50' : batchStatusVal === 'error' ? '#f44336' : primaryColor,
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={batchProgressPct}
                sx={{
                  mb: 2, height: 8, borderRadius: 4,
                  backgroundColor: alpha(primaryColor, 0.15),
                  '& .MuiLinearProgress-bar': { backgroundColor: primaryColor, borderRadius: 4 },
                }}
              />
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: <strong>{batchProcessedItems}/{batchTotalItems}</strong> campaigns ({Math.round(batchProgressPct)}%)
                </Typography>
                {batchFailedItems > 0 && (
                  <Typography variant="body2" sx={{ color: '#f44336' }}>Failed: <strong>{batchFailedItems}</strong></Typography>
                )}
              </Box>
              {batchLogs.length > 0 && (
                <Box sx={{
                  maxHeight: 200, overflowY: 'auto', p: 1.5,
                  bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1,
                  fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.8,
                }}>
                  {batchLogs.map((log, i) => (
                    <Box key={i}>{log}</Box>
                  ))}
                </Box>
              )}
            </Paper>
          )}

          {/* Single mode email result */}
          {activeTab === 0 && renderEmailPreview()}

          {/* MJML mode result */}
          {activeTab === 2 && mjmlResult && (
            <Paper sx={{ p: 3, mt: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>
                    Generated MJML Email
                  </Typography>
                  {mjmlResult.prompt && (
                    <Button
                      size="small"
                      startIcon={<DescriptionIcon fontSize="small" />}
                      onClick={() => setPromptPreviewOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 600, color: primaryColor }}
                    >
                      View Prompt Sent to AI
                    </Button>
                  )}
                  {mjmlResult.items && mjmlResult.items.length > 0 && (
                    <Chip
                      size="small"
                      label={`${mjmlResult.items.length} item${mjmlResult.items.length !== 1 ? 's' : ''} pulled from Items Detail`}
                      sx={{ bgcolor: alpha('#4caf50', 0.12), color: '#2e7d32', fontWeight: 600 }}
                    />
                  )}
                  {mjmlResult.missingItemIds && mjmlResult.missingItemIds.length > 0 && (
                    <Tooltip title={`Missing: ${mjmlResult.missingItemIds.join(', ')}`}>
                      <Chip
                        size="small"
                        label={`${mjmlResult.missingItemIds.length} not found`}
                        sx={{ bgcolor: alpha('#f57c00', 0.15), color: '#e65100', fontWeight: 600 }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <ToggleButtonGroup
                  value={mjmlPreviewMode}
                  exclusive
                  size="small"
                  onChange={(e, v) => { if (v) setMjmlPreviewMode(v); }}
                >
                  <ToggleButton value="preview" sx={{
                    textTransform: 'none', fontWeight: 600, px: 2,
                    '&.Mui-selected': { backgroundColor: alpha(primaryColor, 0.15), color: primaryColor, borderColor: primaryColor },
                  }}>
                    <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} /> Preview
                  </ToggleButton>
                  <ToggleButton value="mjml" sx={{
                    textTransform: 'none', fontWeight: 600, px: 2,
                    '&.Mui-selected': { backgroundColor: alpha(primaryColor, 0.15), color: primaryColor, borderColor: primaryColor },
                  }}>
                    <CodeIcon fontSize="small" sx={{ mr: 0.5 }} /> MJML
                  </ToggleButton>
                  <ToggleButton value="html" sx={{
                    textTransform: 'none', fontWeight: 600, px: 2,
                    '&.Mui-selected': { backgroundColor: alpha(primaryColor, 0.15), color: primaryColor, borderColor: primaryColor },
                  }}>
                    <CodeIcon fontSize="small" sx={{ mr: 0.5 }} /> HTML
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {mjmlResult.notes && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<AutoAwesomeIcon />}>
                  {mjmlResult.notes}
                </Alert>
              )}

              {/* Preview iframe */}
              {mjmlPreviewMode === 'preview' && mjmlResult.html && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 }, mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Zoom:</Typography>
                    <IconButton size="small" onClick={() => setMjmlZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>−</Typography>
                    </IconButton>
                    <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'center', fontWeight: 600 }}>
                      {Math.round(mjmlZoom * 100)}%
                    </Typography>
                    <IconButton size="small" onClick={() => setMjmlZoom((z) => Math.min(2.5, +(z + 0.1).toFixed(2)))}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>+</Typography>
                    </IconButton>
                    <Button size="small" onClick={() => setMjmlZoom(1)} sx={{ textTransform: 'none', fontWeight: 600, color: primaryColor }}>
                      Reset
                    </Button>
                    {[0.5, 0.75, 1, 1.5, 2].map((z) => (
                      <Button
                        key={z}
                        size="small"
                        onClick={() => setMjmlZoom(z)}
                        sx={{
                          textTransform: 'none', minWidth: 0, px: 1, fontWeight: 600,
                          color: mjmlZoom === z ? primaryColor : 'text.secondary',
                          bgcolor: mjmlZoom === z ? alpha(primaryColor, 0.12) : 'transparent',
                        }}
                      >
                        {z * 100}%
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{
                    border: `1px solid ${theme.palette.divider}`, borderRadius: 1,
                    overflow: 'auto', bgcolor: '#fff',
                    height: { xs: 360, sm: 480, md: 600 },
                  }}>
                    <Box sx={{
                      width: `${100 / mjmlZoom}%`,
                      height: { xs: 360 / mjmlZoom, sm: 480 / mjmlZoom, md: 600 / mjmlZoom },
                      transform: `scale(${mjmlZoom})`,
                      transformOrigin: 'top left',
                    }}>
                      <iframe
                        srcDoc={mjmlResult.html}
                        title="MJML Email Preview"
                        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                      />
                    </Box>
                  </Box>
                </>
              )}
              {mjmlPreviewMode === 'preview' && !mjmlResult.html && (
                <Alert severity="warning">No HTML preview was returned. Switch to MJML to view the source.</Alert>
              )}

              {/* MJML source */}
              {mjmlPreviewMode === 'mjml' && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => copyToClipboard(mjmlResult.mjml || '', 'mjml-src')}
                      sx={{
                        textTransform: 'none', fontWeight: 600,
                        color: copiedField === 'mjml-src' ? '#4caf50' : primaryColor,
                      }}
                    >
                      {copiedField === 'mjml-src' ? 'Copied!' : 'Copy MJML'}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<FaDownload />}
                      onClick={downloadMjmlFile}
                      sx={{
                        textTransform: 'none', fontWeight: 600, color: primaryColor,
                      }}
                    >
                      Download .mjml
                    </Button>
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      p: 2, borderRadius: 1,
                      bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5',
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'auto', maxHeight: 500,
                      fontFamily: 'Consolas, Monaco, monospace',
                      fontSize: '0.8rem',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {mjmlResult.mjml || '(no MJML returned)'}
                  </Box>
                </Box>
              )}

              {/* HTML source */}
              {mjmlPreviewMode === 'html' && (
                <Box>
                  <Box sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => copyToClipboard(mjmlResult.html || '', 'html-src')}
                      sx={{
                        textTransform: 'none', fontWeight: 600,
                        color: copiedField === 'html-src' ? '#4caf50' : primaryColor,
                      }}
                    >
                      {copiedField === 'html-src' ? 'Copied!' : 'Copy HTML'}
                    </Button>
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      p: 2, borderRadius: 1,
                      bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5',
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'auto', maxHeight: 500,
                      fontFamily: 'Consolas, Monaco, monospace',
                      fontSize: '0.8rem',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {mjmlResult.html || '(no HTML returned)'}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>
              {activeTab === 2 ? 'MJML Instructions' : 'Instructions'}
            </Typography>
            {activeTab === 2 ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>1. Pick a campaign type — it drives the voice and structure</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>2. For Flash Sale, enter the discount %</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>3. Optionally add extra direction in the prompt box</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>4. Set subject, brand color, CTA URL</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>5. Add image URLs — every one will be used in the layout</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>6. Click Generate MJML Email</Typography>
                <Typography variant="body2">7. Switch between Preview / MJML / HTML and download</Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>1. Select a campaign type from the toggle bar</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>2. Enter the collection or event name</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>3. Optionally add item IDs to feature specific products</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>4. Set discount % for sale or clearance campaigns</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>5. Choose your preferred tone</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>6. Click Generate Email to produce the campaign</Typography>
                <Typography variant="body2">7. Copy individual sections or the full HTML preview</Typography>
              </>
            )}
          </Paper>

          {activeTab === 2 && (
            <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>MJML Tips</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>What is MJML?</strong> A markup language that compiles to responsive HTML emails. Works everywhere — Gmail, Outlook, Apple Mail.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Be specific in your prompt.</strong> Mention sections, image placement, headline copy, CTA text.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Image URLs</strong> must be publicly accessible. Use your AWS File Links tool or the Asset Explorer to grab S3 links.
              </Typography>
              <Typography variant="body2">
                <strong>Compile the MJML</strong> at <span style={{ color: primaryColor, fontWeight: 600 }}>mjml.io/try-it-live</span> to test the final render in your ESP.
              </Typography>
            </Paper>
          )}

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>Campaign Types</Typography>
            {activeTab === 2 ? (
              <>
                {[
                  { label: '48-Hour Flash Sale', desc: 'Urgency play. Punchy, cheeky opener + bold discount. Great for clearing bestsellers fast.' },
                  { label: "What's Trending — Social Proof", desc: 'Frames rugs as what everyone is buying right now. Review counts, ranked picks, high credibility.' },
                  { label: 'One Rug, Totally Different Room', desc: 'Transformation storytelling. Design-forward, sensory language, editorial layout.' },
                  { label: "From the Designer's Desk", desc: 'Curated editorial picks with first-person designer notes. Confident, never salesy.' },
                  { label: 'VIP First-Look — New Arrivals', desc: 'Exclusive early access angle. Makes the reader feel like an insider.' },
                ].map((ct) => (
                  <Box key={ct.label} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>{ct.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{ct.desc}</Typography>
                  </Box>
                ))}
              </>
            ) : (
              CAMPAIGN_TYPES.map((ct) => (
                <Box key={ct.value} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{
                    fontWeight: 700,
                    color: campaignType === ct.value ? primaryColor : 'text.primary',
                  }}>
                    {ct.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {CAMPAIGN_DESCRIPTIONS[ct.value]}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>How It Works</Typography>
            {activeTab === 2 ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  MJML generation uses <strong>Claude Opus 4.7</strong> — the campaign type drives the creative angle, voice, and layout.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Every output includes a valid MJML document plus a parallel table-based HTML preview you can render instantly.
                </Typography>
                <Typography variant="body2">
                  The prompt box is optional — use it for extra direction (featured items, specific copy notes, collections to mention).
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Single and Batch campaigns use Google Gemini to generate email marketing copy tailored to Safavieh brand voice.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Each generation produces 3 A/B testable subject lines, preview text, hero copy, product descriptions, and CTA options.
                </Typography>
                <Typography variant="body2">
                  Use Batch Mode to process multiple campaigns at once from an Excel template.
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Prompt Preview Dialog */}
      <Dialog
        open={promptPreviewOpen}
        onClose={() => setPromptPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Prompt Sent to Claude Opus 4.7</span>
          <Button
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={() => copyToClipboard(mjmlResult?.prompt || '', 'ai-prompt')}
            sx={{ textTransform: 'none', fontWeight: 600, color: copiedField === 'ai-prompt' ? '#4caf50' : primaryColor }}
          >
            {copiedField === 'ai-prompt' ? 'Copied!' : 'Copy'}
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0, p: 2, borderRadius: 1,
              bgcolor: isDarkMode ? alpha('#fff', 0.04) : alpha('#000', 0.03),
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: '0.8rem', lineHeight: 1.5,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: '65vh', overflow: 'auto',
            }}
          >
            {mjmlResult?.prompt || '(no prompt available)'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptPreviewOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, color: primaryColor }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailCampaignBuilder;
